import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, ReplaySubject, filter, switchMap } from 'rxjs';
import { User } from 'src/app/core/models/user.model';
import {
  CommunicationService,
  ThreadWithMessages,
} from 'src/app/core/services/communication.service';
import { UserService } from 'src/app/core/services/user.service';
import { WelcomePopupComponent } from './shared/smart/welcome-popup/welcome-popup.component';
import { ClaimsService, ClaimWithCase } from 'src/app/core/services/claims.service';
import { Case } from 'src/app/core/models/case';

interface WelcomeBannerConfig {
  enabled: boolean;
  size: {
    width: string;
    maxWidth: string;
  };
  theme: {
    primaryColor: string;
    secondaryColor: string;
    headerTextColor: string;
    contentTextColor: string;
    buttonTextColor: string;
  };
  header: {
    imageUrl: {
      pt?: string;
      en?: string;
    };
    descriptionHtml: {
      pt?: string;
      en?: string;
    };
  };
  content: {
    title: {
      pt?: string;
      en?: string;
    };
    descriptionHtml: {
      pt?: string;
      en?: string;
    };
  };
  cta: {
    label: {
      pt?: string;
      en?: string;
    };
  };
}

interface CaseWithBanner {
  case: Case;
  bannerConfig: WelcomeBannerConfig;
  claimId: string;
}

const STORAGE_KEY_PREFIX = 'welcomeBannerSeen_';
const DEFAULT_DIALOG_WIDTH = '500px';
const DEFAULT_DIALOG_MAX_WIDTH = '90vw';
const DEFAULT_DIALOG_MIN_HEIGHT = '400px';

@Component({
  selector: 'app-client',
  templateUrl: './client.component.html',
  styleUrls: ['./client.component.scss'],
})
export class ClientComponent implements OnInit {
  user$!: Observable<User | null>;
  notifications$?: Observable<ThreadWithMessages[]>;

  private userSubject = new ReplaySubject<User | null>(1);

  constructor(
    private cs: CommunicationService,
    private us: UserService,
    private claimsService: ClaimsService,
    public dialog: MatDialog
  ) {
    this.user$ = this.userSubject.asObservable();

    this.notifications$ = this.user$.pipe(
      filter((u): u is User => !!u),
      switchMap((u) => this.cs.listCommunicationsWithMessagesForUser(u.id!))
    );
  }

  async ngOnInit(): Promise<void> {
    const user = await this.us.getCurrentUser();
    this.userSubject.next(user);

    if (!user?.id) {
      return;
    }

    const claims = await this.claimsService.getMyClaimsWithCase(100, 0);
    const caseWithBanner = await this.findUnseenBannerCase(claims, user.id);

    if (caseWithBanner) {
      this.openWelcomeBannerDialog(caseWithBanner, user.id);
    }
  }

  private async findUnseenBannerCase(
    claims: ClaimWithCase[],
    userId: string
  ): Promise<CaseWithBanner | null> {
    for (const claim of claims) {
      const caseData = claim.cp_case;
      if (!caseData?.configuration?.welcome_banner) {
        continue;
      }

      const bannerConfig = caseData.configuration.welcome_banner as WelcomeBannerConfig;
      if (!bannerConfig?.enabled) {
        continue;
      }

      const welcomeKey = this.getWelcomeBannerStorageKey(userId, caseData.id);
      const hasSeenBanner = localStorage.getItem(welcomeKey);

      if (hasSeenBanner === 'true') {
        continue;
      }

      if (!this.hasBannerContent(bannerConfig)) {
        continue;
      }

      // Só exibe o banner enquanto houver uma assinatura (Legal Pack) pendente
      // — quem já assinou está fora do onboarding inicial, então o welcome
      // popup não deve mais aparecer mesmo que restem questionários/KYC.
      const stillNeedsToSign = await this.claimsService.hasOutstandingSignatureActions(claim.id!);
      if (!stillNeedsToSign) {
        continue;
      }

      return { case: caseData, bannerConfig, claimId: claim.id! };
    }

    return null;
  }

  private hasBannerContent(bannerConfig: WelcomeBannerConfig): boolean {
    const hasHeaderDesc = this.getLocalizedContent(
      bannerConfig.header?.descriptionHtml
    );
    const hasContentTitle = this.getLocalizedContent(bannerConfig.content?.title);
    const hasContentDesc = this.getLocalizedContent(
      bannerConfig.content?.descriptionHtml
    );
    const hasCtaLabel = this.getLocalizedContent(bannerConfig.cta?.label);

    return !!(hasHeaderDesc || hasContentTitle || hasContentDesc || hasCtaLabel);
  }

  private getLocalizedContent(
    content: string | { pt?: string; en?: string } | undefined
  ): string | undefined {
    if (!content) {
      return undefined;
    }

    if (typeof content === 'string') {
      return content;
    }

    return content.pt || content.en;
  }

  private getWelcomeBannerStorageKey(userId: string, caseId: string): string {
    return `${STORAGE_KEY_PREFIX}${userId}_${caseId}`;
  }

  private openWelcomeBannerDialog(
    caseWithBanner: CaseWithBanner,
    userId: string
  ): void {
    const welcomeKey = this.getWelcomeBannerStorageKey(
      userId,
      caseWithBanner.case.id
    );
    localStorage.setItem(welcomeKey, 'true');

    const dialogRef = this.dialog.open(WelcomePopupComponent, {
      data: {
        bannerConfig: caseWithBanner.bannerConfig,
        caseId: caseWithBanner.case.id,
        caseCode: caseWithBanner.case.code,
      },
      width: caseWithBanner.bannerConfig.size?.width || DEFAULT_DIALOG_WIDTH,
      maxWidth:
        caseWithBanner.bannerConfig.size?.maxWidth || DEFAULT_DIALOG_MAX_WIDTH,
      minHeight: DEFAULT_DIALOG_MIN_HEIGHT,
      disableClose: false,
      panelClass: 'welcome-banner-dialog',
    });

    dialogRef.afterClosed().subscribe(() => {
      localStorage.setItem(welcomeKey, 'true');
    });
  }
}
